import React, { useContext, useMemo, useCallback, useState } from 'react';
import { useLocalStorage } from 'react-use';

type SidePanelContextValue = {
  menuOpen: boolean;
  openMenu: () => void;
  closeMenu: () => void;
  pinned?: boolean;
  togglePinned: () => void;
};

const SidePanelContext = React.createContext<SidePanelContextValue | null>(
  null
);

export const useSidePanelContext = (): SidePanelContextValue => {
  const val = useContext(SidePanelContext);
  if (!val) {
    throw new Error(
      'useSidePanelContext must be called within a SidePanelContextProvider'
    );
  }

  return val;
};

type SidePanelContextProviderProps = {
  children: React.ReactNode;
};

export const SidePanelContextProvider: React.FC<SidePanelContextProviderProps> = ({
  children
}: SidePanelContextProviderProps) => {
  const [pinned = true, setIsPinned] = useLocalStorage('menu-pinned', true);
  const togglePinned = useCallback(() => setIsPinned(!pinned), [
    setIsPinned,
    pinned
  ]);
  const [menuOpen, setMenuOpen] = useState(false);
  const openMenu = useCallback(() => setMenuOpen(true), [setMenuOpen]);
  const closeMenu = useCallback(() => setMenuOpen(false), [setMenuOpen]);

  const value = useMemo(
    () => ({
      menuOpen,
      openMenu,
      closeMenu,
      pinned,
      togglePinned
    }),
    [menuOpen, openMenu, closeMenu, pinned, togglePinned]
  );

  return (
    <SidePanelContext.Provider value={value}>
      {children}
    </SidePanelContext.Provider>
  );
};

export default SidePanelContextProvider;
